import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  configSchema,
  deepHealthSchema,
  healthSchema,
  maintenanceActionSchema,
  maintenanceResponseSchema,
  okSchema,
  updateConfigSchema,
  type MaintenanceAction,
  type UpdateConfigInput,
} from './schemas';

const keys = {
  config: () => ['config', 'current'] as const,
  health: () => ['config', 'health'] as const,
  deepHealth: () => ['config', 'health', 'deep'] as const,
};

export function useConfig() {
  return useQuery({
    queryKey: keys.config(),
    queryFn: ({ signal }) => api('/api/config', { schema: configSchema, signal }),
    staleTime: 60_000,
  });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateConfigInput) =>
      api('/api/config', {
        method: 'POST',
        body: updateConfigSchema.parse(input),
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.config() });
    },
  });
}

export function useHealth() {
  return useQuery({
    queryKey: keys.health(),
    queryFn: ({ signal }) => api('/api/health', { schema: healthSchema, signal }),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useDeepHealth() {
  return useMutation({
    mutationFn: () => api('/api/health/deep', { schema: deepHealthSchema }),
  });
}

export function useMaintenance() {
  return useMutation({
    mutationFn: (action: MaintenanceAction) =>
      api('/api/config/maintenance', {
        method: 'POST',
        body: { action: maintenanceActionSchema.parse(action) },
        schema: maintenanceResponseSchema,
      }),
  });
}
